import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

const EMPTY = { name: '', description: '', discountPercent: '', active: true };
const money = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const messageOf = (e, fallback) => e.response?.data?.message || (typeof e.response?.data === 'string' ? e.response.data : null) || fallback;

/**
 * Sales > Price Lists (Org Admin and Sales Manager, only when the organization has price lists on): a list is fixed prices per product plus an optional
 * percentage off the standard price for the rest. A customer is given a list on the customer form, and quotations, sale orders and sale bills then start from
 * its price.
 */
export default function PriceLists({ active }) {
  const [lists, setLists] = useState(null);
  const [editing, setEditing] = useState(null); // null, 'new' or a list
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState(null); // the list whose prices are open
  const [rows, setRows] = useState([]);
  const [typed, setTyped] = useState({});
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/price-lists');
      setLists(r.data?.lists || []);
    } catch (e) {
      setLists([]);
      toast.error(messageOf(e, 'Could not load the price lists.'));
    }
  }, []);
  useEffect(() => { if (active) load(); }, [active, load]);

  const openForm = (list) => {
    setEditing(list || 'new');
    setForm(list ? { name: list.name, description: list.description || '', discountPercent: list.discountPercent ?? '', active: list.active } : EMPTY);
  };

  const saveList = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { name: form.name, description: form.description, discountPercent: form.discountPercent === '' ? null : Number(form.discountPercent), active: form.active };
    try {
      if (editing === 'new') await axios.post('/api/price-lists', body);
      else await axios.put(`/api/price-lists/${editing.id}`, body);
      toast.success('Saved.');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(messageOf(err, 'Could not save the price list.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (list) => {
    if (!window.confirm(`Delete the price list "${list.name}" and its prices?`)) return;
    try {
      await axios.delete(`/api/price-lists/${list.id}`);
      toast.success('Deleted.');
      await load();
    } catch (err) {
      toast.error(messageOf(err, 'Could not delete the price list.'));
    }
  };

  const openPrices = async (list) => {
    setPricing(list);
    setRows([]);
    setTyped({});
    setQuery('');
    try {
      const r = await axios.get(`/api/price-lists/${list.id}`);
      setRows(r.data?.products || []);
      setTyped(Object.fromEntries((r.data?.products || []).filter((p) => p.fixedPrice != null).map((p) => [p.productId, String(p.fixedPrice)])));
    } catch (err) {
      toast.error(messageOf(err, 'Could not load the prices.'));
      setPricing(null);
    }
  };

  const savePrices = async () => {
    setBusy(true);
    try {
      const prices = Object.entries(typed).filter(([, v]) => v !== '' && v != null).map(([productId, v]) => ({ productId: Number(productId), price: Number(v) }));
      await axios.put(`/api/price-lists/${pricing.id}/prices`, { prices });
      toast.success('Prices saved.');
      setPricing(null);
      await load();
    } catch (err) {
      toast.error(messageOf(err, 'Could not save the prices.'));
    } finally {
      setBusy(false);
    }
  };

  const shown = useMemo(() => {
    const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return rows.filter((p) => words.every((w) => `${p.name} ${p.sku || ''}`.toLowerCase().includes(w)));
  }, [rows, query]);

  if (lists === null) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  return (
    <div data-testid="price-lists">
      <div className="d-flex justify-content-between align-items-start mb-3 gap-3">
        <p className="text-muted mb-0">
          A price list gives a kind of customer their own prices: fixed prices for some products, and if you like a percentage off the standard price for all the rest.
          Give a customer a list on the customer&apos;s form; their quotations, sale orders and sale bills then start from its prices (before GST), which you can still change on a line.
        </p>
        <Button className="text-nowrap" onClick={() => openForm(null)} data-testid="new-price-list">+ New price list</Button>
      </div>

      {lists.length === 0 ? (
        <Alert variant="light" className="border">No price lists yet. Make one for each kind of customer who gets special prices, for example Wholesale or Dealers.</Alert>
      ) : (
        <Table hover responsive size="sm" className="align-middle">
          <thead><tr><th>Name</th><th className="text-end">% off the rest</th><th className="text-end">Fixed prices</th><th className="text-end">Customers</th><th /></tr></thead>
          <tbody>
            {lists.map((l) => (
              <tr key={l.id}>
                <td>{l.name} {!l.active && <Badge bg="secondary">Off</Badge>}{l.description && <div className="small text-muted">{l.description}</div>}</td>
                <td className="text-end">{l.discountPercent != null ? `${Number(l.discountPercent)}%` : '-'}</td>
                <td className="text-end">{l.fixedPrices}</td>
                <td className="text-end">{l.customers}</td>
                <td className="text-end text-nowrap">
                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openPrices(l)}>Prices</Button>
                  <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openForm(l)}>Edit</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => remove(l)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={Boolean(editing)} onHide={() => setEditing(null)} centered>
        <Form onSubmit={saveList}>
          <Modal.Header closeButton><Modal.Title>{editing === 'new' ? 'New price list' : 'Edit price list'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} required placeholder="For example: Wholesale" data-testid="price-list-name" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={300} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Percent off the standard price (optional)</Form.Label>
              <Form.Control type="number" min="0" max="100" step="0.01" value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: e.target.value })} data-testid="price-list-discount" />
              <Form.Text className="text-muted">Applies to every product you have not given a fixed price in this list. Empty means those products keep their usual price.</Form.Text>
            </Form.Group>
            <Form.Check type="switch" id="price-list-active" label="In use" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <Form.Text className="text-muted">A list that is not in use gives no prices, but keeps them and its customers.</Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" disabled={saving}>Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(pricing)} onHide={() => setPricing(null)} size="lg" centered scrollable>
        <Modal.Header closeButton><Modal.Title>Prices in {pricing?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Type a price (before GST) for each product that has its own price in this list. Leave it empty to use
            {pricing?.discountPercent != null ? ` the standard price less ${Number(pricing.discountPercent)}%` : ' the usual price'}; that price is shown in grey.
          </p>
          <Form.Control type="search" placeholder="Search products" value={query} onChange={(e) => setQuery(e.target.value)} className="mb-2" />
          {rows.length === 0 ? <div className="text-center py-4"><Spinner animation="border" size="sm" /></div> : (
            <Table size="sm" className="align-middle">
              <thead><tr><th>Product</th><th className="text-end">Standard price</th><th style={{ width: 170 }}>Price in this list</th></tr></thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.productId}>
                    <td>{p.name} {!p.active && <Badge bg="secondary">Inactive</Badge>}<div className="small text-muted">{p.sku}</div></td>
                    <td className="text-end">{p.basePrice != null ? money.format(p.basePrice) : '-'}</td>
                    <td>
                      <Form.Control size="sm" type="number" min="0" step="0.01" value={typed[p.productId] ?? ''} aria-label={`Price of ${p.name}`}
                        onChange={(e) => setTyped({ ...typed, [p.productId]: e.target.value })}
                        placeholder={p.fixedPrice == null && p.listPrice != null ? money.format(p.listPrice) : ''} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPricing(null)}>Cancel</Button>
          <Button onClick={savePrices} disabled={busy} data-testid="save-prices">Save prices</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
